/**
 * ClipButton — small clip-icon affordance for estimate/WO fields.
 *
 * Sub-spec J, Block J-C.
 *
 * Renders a small paperclip icon button next to an editable field.
 * Tapping it activates clip mode (POSTs to /api/clip/target, pairs with
 * the extension, then shows "waiting for clip…" state).
 *
 * Owner/advisor only (pass isAllowed from a useHasRole gate).
 * Feature-flagged by REACT_APP_WEB_CLIP — returns null when disabled.
 *
 * WP-06 Part 1 — this button is NOT rendered at all when the browser cannot host
 * the extension (mobile has no extension runtime, so "Waiting for clip…" could
 * never resolve there), and the waiting state now always terminates: it offers
 * Cancel while waiting and a stated failure with Try again / manual entry when
 * the pairing or the clip times out.
 *
 * Provides data-testids for J-D Playwright:
 *   clip-icon-<field>      — the trigger button
 *   clip-waiting-<field>   — the "waiting…" badge
 *   clip-cancel-<field>    — cancel the wait
 *   clip-failed-<field>    — terminal failure notice
 *   clip-retry-<field>     — retry after a failure
 *   clip-manual-<field>    — manual-entry fallback after a failure
 *   clip-filled-<field>    — the filled-value confirm row
 */

import React from 'react';
import { Button, Tooltip, Tag, Space, Typography } from 'antd';
import {
  PaperClipOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { ClipTargetField, CLIP_FIELD_LABELS } from '../../services/clipApi';
import { UseClipResult } from '../../hooks/useClip';

const { Text } = Typography;

interface ClipButtonProps {
  /** Which field this button controls. */
  field: ClipTargetField;
  /** The record this estimate or work order belongs to. */
  recordType: 'estimate' | 'work_order';
  recordId: number;
  /** Clip hook result from useClip() — shared by all clip buttons on the page. */
  clip: UseClipResult;
  /** Role gate — set true for owner/advisor, false otherwise. */
  isAllowed: boolean;
  /**
   * Called when a clip:filled value arrives for this specific field.
   * The caller should update its local form state with the new value.
   */
  onFilled?: (value: string) => void;
  /**
   * Manual fallback offered when a clip attempt fails (e.g. open the paste-and-parse
   * capture drawer). Omit and no manual button is shown.
   */
  onManualEntry?: () => void;
  /**
   * Short visible label on the trigger (e.g. "Make"). Needed wherever several clip
   * buttons sit side by side — identical paperclips are indistinguishable.
   */
  compactLabel?: string;
}

const ClipButton: React.FC<ClipButtonProps> = ({
  field,
  recordType,
  recordId,
  clip,
  isAllowed,
  onFilled,
  onManualEntry,
  compactLabel,
}) => {
  // Feature-flagged off and role-gated.
  // Read at render time (not module-load) so tests can set the env var before
  // rendering without needing to resetModules.
  const webClipEnabled = process.env.REACT_APP_WEB_CLIP === 'true';
  if (!webClipEnabled || !isAllowed) return null;

  const { state, extensionSupported, activateClip, retryClip, clearClip, clearFilled, clearFailure } = clip;
  const label = CLIP_FIELD_LABELS[field];
  const isWaiting = state.waitingField === field;
  const isFilled = state.filledField === field;
  const isFailed = state.failedField === field;
  const isLoading = isWaiting && state.isPending;

  // No extension runtime here (mobile) or a pair attempt already proved it
  // absent: never offer the trigger. Any in-flight outcome for this field still
  // renders so the user sees how their last attempt ended.
  if (!extensionSupported && !isWaiting && !isFilled && !isFailed) return null;

  const handleClick = async () => {
    if (isWaiting) return; // already waiting for this field — no-op
    try {
      await activateClip(recordType, recordId, field);
    } catch (err: any) {
      // Caller surface (EstimateDetail) can catch; ClipButton just logs.
      console.error('[ClipButton] activateClip failed:', err?.message);
    }
  };

  const handleRetry = async () => {
    try {
      await retryClip(recordType, recordId, field);
    } catch (err: any) {
      console.error('[ClipButton] retryClip failed:', err?.message);
    }
  };

  const handleConfirm = () => {
    if (state.filledValue !== null && onFilled) {
      onFilled(state.filledValue);
    }
    clearFilled();
  };

  const handleDismiss = () => {
    clearFilled();
  };

  // Filled state — show confirm row
  if (isFilled && state.filledValue !== null) {
    return (
      <Space
        size="small"
        wrap
        data-testid={`clip-filled-${field}`}
        style={{ marginTop: 4 }}
      >
        <Tag color="blue" icon={<CheckCircleOutlined />}>
          Clipped: <Text strong>{state.filledValue}</Text>
        </Tag>
        <Button size="small" type="primary" onClick={handleConfirm}>
          Keep
        </Button>
        <Button size="small" onClick={handleDismiss}>
          Dismiss
        </Button>
      </Space>
    );
  }

  // Terminal failure state — the wait ALWAYS ends here or in the filled state.
  if (isFailed) {
    const failureText =
      state.failureReason === 'extension_unreachable'
        ? 'CNF extension not responding'
        : `No ${label} clipped in time`;
    return (
      <Space
        size="small"
        wrap
        data-testid={`clip-failed-${field}`}
        style={{ marginTop: 4 }}
      >
        <Tooltip
          title={
            state.failureReason === 'extension_unreachable'
              ? 'The CNF browser extension did not answer. Install or enable it in this browser, or enter the value manually.'
              : 'Nothing was clipped before the clip session expired. Start again or enter the value manually.'
          }
        >
          <Tag color="warning" icon={<ExclamationCircleOutlined />} style={{ margin: 0 }}>
            {failureText}
          </Tag>
        </Tooltip>
        <Button size="small" onClick={handleRetry} data-testid={`clip-retry-${field}`}>
          Try again
        </Button>
        {onManualEntry && (
          <Button
            size="small"
            type="primary"
            onClick={onManualEntry}
            data-testid={`clip-manual-${field}`}
          >
            Enter manually
          </Button>
        )}
        <Button size="small" type="text" onClick={clearFailure} data-testid={`clip-dismiss-${field}`}>
          Dismiss
        </Button>
      </Space>
    );
  }

  // Waiting state — "waiting for clip…" badge, always cancellable so the user is
  // never stuck, and always bounded by the hook's hard timeout.
  if (isWaiting) {
    return (
      <Space size={4} wrap data-testid={`clip-waiting-wrap-${field}`} style={{ marginLeft: 4 }}>
        <Tag
          icon={<LoadingOutlined spin />}
          color="processing"
          data-testid={`clip-waiting-${field}`}
          style={{ cursor: 'default', margin: 0 }}
        >
          Waiting for clip…
        </Tag>
        <Button size="small" type="text" onClick={clearClip} data-testid={`clip-cancel-${field}`}>
          Cancel
        </Button>
      </Space>
    );
  }

  // Idle state — show clip icon button.
  // Default (bordered) button at full opacity so the entry point is discoverable —
  // the old faint text-button was routinely missed by users.
  return (
    <Tooltip
      title={`Clip ${label} from another website`}
      placement="top"
    >
      <Button
        size="small"
        icon={<PaperClipOutlined />}
        onClick={handleClick}
        loading={isLoading}
        data-testid={`clip-icon-${field}`}
        aria-label={`Clip ${label} from another website`}
      >
        {compactLabel}
      </Button>
    </Tooltip>
  );
};

export default ClipButton;
